/**
 * **"Single academy" mode** — TS mirror of `packages/core-rust/src/tenancy/mod.rs`.
 *
 * The two runtimes read **the same data file**
 * (`./modes.json`): Rust via `include_str!`, TypeScript via this `import`.
 * There can't be a SINGLE implementation (they're two languages), but there can
 * be ONE registry — the precedent is `packages/shared/src/admin/rbac-contract.json`.
 *
 * ── Division of responsibilities ─────────────────────────────────────────────
 *
 * **The server DENIES** (create, join another, switch: see the resolution in
 * `core_rust::tenancy`). **This half only HIDES** what the server is going to
 * reject. If the two drift apart, the worst that happens is a button that gives
 * a 403 — never an open door. That's why there's no security decision here.
 *
 * ── Fail-closed ──────────────────────────────────────────────────────────────
 *
 * Missing, blank, unknown key, or unreadable registry ⇒ `single`.
 *
 * ── Runtime, not build-time (unlike the AI tutor cutoff) ─────────────────────
 *
 * The tutor cutoff is read at compile time on purpose: an already-published
 * store binary must not be able to switch on a third-party data handoff. Here
 * it's the opposite — the requirement is that **unfreezing must be a config
 * change, not a deploy** — so the key comes from `/config.js` (`__APP_CONFIG__`,
 * the same lever as `SERVER_URL` and `VOCABULARY_PROFILE`), falling back to
 * `import.meta.env.VITE_ORG_TENANCY_MODE` for Tauri and dev, where there's no
 * nginx entrypoint to write anything.
 *
 * Record: `docs/auditoria/2026-08-15-modo-academia-unica.md`.
 */

import modesData from "./modes.json";

// ─── Types ────────────────────────────────────────────────────────────────────

/** Self-descriptive metadata (same shape as `FeatureManifest`). */
export interface TenancyManifest {
  key: string;
  name: string;
  description: string;
  scope: "platform" | "org" | "course";
  status: "stable" | "beta" | "experimental";
  since?: string;
}

/** The four limits of a mode. Orthogonal: not a boolean with two names. */
export interface TenancyLimits {
  /** Simultaneous academies per person. `0` = no limit. */
  readonly maxOrganizationsPerUser: number;
  /** Academies this deployment declares having. `0` = no limit. */
  readonly maxOrganizationsInDeployment: number;
  /** Can an authenticated user create an academy? */
  readonly allowCreateOrganization: boolean;
  /** Can they switch their session's active academy? */
  readonly allowSwitchOrganization: boolean;
}

export interface TenancyMode {
  readonly key: string;
  readonly name: string;
  readonly description: string;
  readonly manifest: TenancyManifest;
  readonly limits: TenancyLimits;
}

// ─── Registry construction (validated, not cast) ─────────────────────────────

/**
 * Rebuilt field by field instead of cast because the file is written by a
 * person (it's CONFIGURATION): an `as TenancyMode` would accept a missing key
 * and the absent limit would read as `undefined`, which in a numeric
 * comparison is `false` — i.e. it would open what should be closed.
 * Same criterion as `assertProfile` in the vocabulary registry.
 */
function bool(bag: Record<string, unknown>, field: string, source: string): boolean {
  const v = bag[field];
  if (typeof v !== "boolean") {
    throw new TypeError(`[tenancy] ${source}: \`${field}\` no es booleano`);
  }
  return v;
}

function int(bag: Record<string, unknown>, field: string, source: string): number {
  const v = bag[field];
  if (typeof v !== "number" || !Number.isInteger(v)) {
    throw new TypeError(`[tenancy] ${source}: \`${field}\` no es entero`);
  }
  return v;
}

function str(bag: Record<string, unknown>, field: string, source: string): string {
  const v = bag[field];
  if (typeof v !== "string" || v.trim() === "") {
    throw new Error(`[tenancy] ${source}: missing \`${field}\``);
  }
  return v;
}

function assertMode(key: string, raw: unknown): TenancyMode {
  const source = `modes.${key}`;
  if (typeof raw !== "object" || raw === null) {
    throw new Error(`[tenancy] ${source}: is not an object`);
  }
  const bag: Record<string, unknown> = { ...raw };
  if (str(bag, "key", source) !== key) {
    throw new Error(`[tenancy] ${source}: object key and content key do not match`);
  }
  const rawLimits = bag.limits;
  if (typeof rawLimits !== "object" || rawLimits === null) {
    throw new Error(`[tenancy] ${source}: missing \`limits\``);
  }
  const limitsBag: Record<string, unknown> = { ...rawLimits };
  const rawManifest = bag.manifest;
  if (typeof rawManifest !== "object" || rawManifest === null) {
    throw new Error(`[tenancy] ${source}: missing \`manifest\``);
  }
  const manifestBag: Record<string, unknown> = { ...rawManifest };
  return {
    key,
    name: str(bag, "name", source),
    description: str(bag, "description", source),
    manifest: {
      key: str(manifestBag, "key", `${source}.manifest`),
      name: str(manifestBag, "name", `${source}.manifest`),
      description: str(manifestBag, "description", `${source}.manifest`),
      scope: "platform",
      status: manifestBag.status === "experimental" ? "experimental" : "stable",
      since: typeof manifestBag.since === "string" ? manifestBag.since : undefined,
    },
    limits: {
      maxOrganizationsPerUser: int(limitsBag, "maxOrganizationsPerUser", source),
      maxOrganizationsInDeployment: int(limitsBag, "maxOrganizationsInDeployment", source),
      allowCreateOrganization: bool(limitsBag, "allowCreateOrganization", source),
      allowSwitchOrganization: bool(limitsBag, "allowSwitchOrganization", source),
    },
  };
}

/**
 * The CODE default, twin of `builtin_single()` in Rust. Only used if the entire registry
 * is unreadable. There's a test asserting it says the same as the `single` row in the
 * JSON: if they drifted apart, the fail-closed would open a door.
 */
const BUILTIN_SINGLE: TenancyMode = {
  key: "single",
  name: "Academia única",
  description: "Defecto de código (registro ilegible): una sola academia, congelada.",
  manifest: {
    key: "academy.tenancy.single",
    name: "Tenencia · academia única",
    description: "Defecto de código.",
    scope: "platform",
    status: "stable",
  },
  limits: {
    maxOrganizationsPerUser: 1,
    maxOrganizationsInDeployment: 1,
    allowCreateOrganization: false,
    allowSwitchOrganization: false,
  },
};

interface RegistryShape {
  modes: Record<string, TenancyMode>;
  defaultKey: string;
}

export function buildTenancyRegistry(raw: unknown): RegistryShape {
  if (typeof raw !== "object" || raw === null) {
    throw new Error("[tenancy] modes.json: is not an object");
  }
  const doc: Record<string, unknown> = { ...raw };
  const defaultKey = str(doc, "defaultMode", "modes.json").trim();
  const rawModes = doc.modes;
  if (typeof rawModes !== "object" || rawModes === null) {
    throw new Error("[tenancy] modes.json: missing `modes`");
  }
  const modes: Record<string, TenancyMode> = {};
  for (const [key, node] of Object.entries({ ...rawModes })) {
    modes[key] = assertMode(key, node);
  }
  if (!modes[defaultKey]) {
    throw new Error(`[tenancy] modes.json: default mode "${defaultKey}" is not declared`);
  }
  return { modes, defaultKey };
}

function loadRegistry(): RegistryShape {
  try {
    return buildTenancyRegistry(modesData);
  } catch {
    // Unreadable registry ⇒ the deployment is a SINGLE academy. Closed, not broken.
    return { modes: { single: BUILTIN_SINGLE }, defaultKey: "single" };
  }
}

const REGISTRY = loadRegistry();

/** Declared modes, indexed by key (== `SCHEMES` / `VOCABULARY_PROFILES`). */
export const TENANCY_MODES: Readonly<Record<string, TenancyMode>> = REGISTRY.modes;

/** Stable order of modes (for selects and catalog). */
export const TENANCY_MODE_KEYS: readonly string[] = Object.keys(TENANCY_MODES).toSorted();

/** Default mode key (== `DEFAULT_GRADING_SCHEME_KEY`). */
export const DEFAULT_TENANCY_MODE_KEY: string = REGISTRY.defaultKey;

/** Server environment key (mirror of `tenancy::ENV_KEY`). */
export const TENANCY_ENV_KEY = "ORG_TENANCY_MODE";

/** Client build key (mirror of `tenancy::CLIENT_ENV_KEY`). */
export const TENANCY_CLIENT_ENV_KEY = "VITE_ORG_TENANCY_MODE";

/**
 * Returns the mode for a key, or the default if it's unknown
 * (== `gradingScheme` / `vocabularyProfile`). **Fail-closed.**
 */
export function tenancyMode(key: string | null | undefined): TenancyMode {
  const k = (key ?? "").trim();
  return TENANCY_MODES[k] ?? TENANCY_MODES[DEFAULT_TENANCY_MODE_KEY] ?? BUILTIN_SINGLE;
}

// ─── Active mode (runtime config, no rebuild) ────────────────────────────────

interface TenancyRuntimeConfig {
  ORG_TENANCY_MODE?: string;
}

function readRuntimeKey(): string | undefined {
  const runtime =
    (globalThis as typeof globalThis & { __APP_CONFIG__?: TenancyRuntimeConfig }).__APP_CONFIG__ ??
    {};
  let fromVite: unknown;
  try {
    // `import.meta.env` doesn't exist outside Vite (bun tests, native
    // packaging). Same pattern as `ai-release.ts`.
    fromVite = (import.meta as { env?: Record<string, unknown> }).env?.[TENANCY_CLIENT_ENV_KEY];
  } catch {
    fromVite = undefined;
  }
  for (const candidate of [runtime.ORG_TENANCY_MODE, fromVite]) {
    if (typeof candidate === "string" && candidate.trim() !== "") {
      return candidate;
    }
  }
  return undefined;
}

/** This client's active mode. */
export function activeTenancyMode(): TenancyMode {
  return tenancyMode(readRuntimeKey());
}

// ─── Questions the UI asks ───────────────────────────────────────────────────
//
// Three named functions instead of every screen reading `limits` on its own:
// that's what stops the criterion from being (wrongly) re-derived in each place.

/** Should the "create academy" button be shown? (the server decides for real). */
export function canCreateOrganization(mode: TenancyMode = activeTenancyMode()): boolean {
  return mode.limits.allowCreateOrganization;
}

/** Can someone with `current` academies join another one? */
export function canJoinAnotherOrganization(
  currentMemberships: number,
  mode: TenancyMode = activeTenancyMode(),
): boolean {
  const max = mode.limits.maxOrganizationsPerUser;
  return max <= 0 || currentMemberships < max;
}

/**
 * Should the academy selector be shown?
 *
 * **With a single academy, the selector was ALREADY hidden** by its own
 * `orgs().length > 1` (`features/academy/components/org-switcher.tsx`), which
 * is the normal case for this mode. This does NOT duplicate that condition: it
 * answers the other half — "the mode forbids switching" — which that one has
 * no way to know. The two combine in the selector's `Show`, they don't repeat.
 */
export function canSwitchOrganization(mode: TenancyMode = activeTenancyMode()): boolean {
  return mode.limits.allowSwitchOrganization;
}

/**
 * The case that must not pass silently: the mode says one academy and there's
 * more in the data. Returns the warning, or `undefined` if everything checks out.
 *
 * Mirror of `tenancy::check_deployment`. The server shouts it on startup; the
 * UI paints it where the selector would have been, which is exactly where
 * an admin is going to see it.
 */
export function tenancyMismatch(
  visibleOrganizations: number,
  mode: TenancyMode = activeTenancyMode(),
): { declaredMax: number; actual: number } | undefined {
  const declaredMax = mode.limits.maxOrganizationsInDeployment;
  if (declaredMax <= 0 || visibleOrganizations <= declaredMax) {
    return undefined;
  }
  return { declaredMax, actual: visibleOrganizations };
}
